import { IItinerarymap, IResponse, SectionId, UserOrganisation } from "@eventr/common";
import { DataAPI } from "data/Data";
import { createContext, useContext } from "react";
import { getAllDayOfDate } from "utils";
import { createStore, useStore } from "zustand";
import { useGuestStore } from "../guest.store";
import { guestVisibleActivities } from "./guest-visible-activities";

type TRangeType = {
  all: boolean;
  startDate: string | undefined;
  endDate: string | undefined;
};

interface DuplicateState {
  overrideName: string;
  setOverrideName: (name: string) => void;
  selectedOrg: UserOrganisation | undefined;
  setOrg: (org: UserOrganisation, itinMap: IItinerarymap[]) => void;
  orgItins: IItinerarymap[];
  duplicateItinerary: () => Promise<IResponse<number>>;
  selectedItin: IItinerarymap;
  setSelectedItin: (setItin: IItinerarymap) => void;
  range: TRangeType;
  setRange: (setRange: TRangeType) => void;
}

type DuplicateStore = ReturnType<typeof createDuplicateStore>;

export const createDuplicateStore = () => {
  return createStore<DuplicateState>()((set, get) => ({
    overrideName: "",
    setOverrideName: (name) => set({ overrideName: name }),

    selectedOrg: undefined,
    orgItins: [],
    setOrg: (org, itinMap) => set({ selectedOrg: org, orgItins: itinMap }),
    duplicateItinerary: async () => {
      const guestData = useGuestStore.getState().guestData;
      const itinerary = guestData?.itinerary
        ? {
            ...guestData.itinerary,
            v2: guestData.itinerary.v2?.map((section) =>
              section.sectionID === SectionId.ACTIVITY
                ? {
                    ...section,
                    data: { ...section.data, activities: guestVisibleActivities(section.data?.activities) },
                  }
                : section,
            ),
          }
        : guestData?.itinerary;
      let body: any = {
        map_entry: guestData?.map_entry,
        itinerary,
        categories: guestData?.org_config?.categories,
        name_override: get().overrideName,
        itin_to_copy_to: get().selectedItin.id,
      };

      /// Add in the ranges if needed.
      if (!get().range.all) {
        body = {
          ...body,
          startRangeDateTime: get().range.startDate,
          endRangeDateTime: get().range.endDate,
        };
      }

      return await DataAPI.guest.duplicateItinerary(get().selectedOrg?.name ?? "", body);
    },
    selectedItin: {
      id: -1,
      end_time: 0,
      start_time: 0,
      itinerary_name: "",
      last_updated: 0,
      itinerary_version: "",
      itinerary_endpoint: "",
      tags: [],
    },
    setSelectedItin: (setItin: IItinerarymap) => {
      set({ selectedItin: setItin });
    },
    range: {
      all: true,
      startDate: getAllDayOfDate(useGuestStore.getState().guestData?.itinerary?.v2?.[0]?.data.start_date, "start"),
      endDate: getAllDayOfDate(useGuestStore.getState().guestData?.itinerary?.v2?.[0]?.data.end_date, "end"),
    },
    setRange: (setRange: TRangeType) => {
      set({ range: setRange });
    },
  }));
};
export const DuplicateContext = createContext<DuplicateStore | null>(null);
export function useDuplicateContext<T>(selector: (state: DuplicateState) => T): T {
  const store = useContext(DuplicateContext);

  if (!store) throw new Error("Missing DuplicateContext.Provider in the tree");
  return useStore(store, selector);
}
export function useDuplicateStore() {
  const store = useContext(DuplicateContext);

  if (!store) throw new Error("Missing DuplicateContext.Provider in the tree");
  return store;
}
